/**
 * How tall a bathroom wall's vanity splash and tub backsplash run, by the
 * wall's own setting (`wall.backsplashSpec.vanity` / `.tub`, contractor call
 * #9 step 3). The rule is `@shared-lib/kitchen-assembly/styling/backsplashSpec`;
 * this adds what only the browser knows about a bathroom wall — which mirror
 * hangs over the splash — so every place that draws the bands asks one place.
 *
 * No setting is today's bands exactly: 4" over a vanity, 12" over a tub.
 */
import {
  readBacksplashSpec,
  vanityBandHeightM,
  tubBandHeightM,
  wallTopY,
} from '@shared-lib/kitchen-assembly/styling/backsplashSpec';
import { isMirror } from './objectTypeUtils';
import { belongsToWall } from './parentWallHelpers';

/**
 * The underside of the lowest mirror hung on this wall over the splash, or
 * null. "Over the splash" is the caller's along-wall test, since the two
 * callers measure along the wall in different frames (the room's walls, or
 * the wall mesh).
 *
 * @param {Object} room
 * @param {string} wallId
 * @param {(mirror: Object) => boolean} overSplash
 * @param {number} counterTopY world Y of the vanity's counter top
 */
export function mirrorBottomOverSplash(room, wallId, overSplash, counterTopY) {
  let lowest = null;
  for (const obj of room?.objects || []) {
    if (!isMirror(obj) || !obj.center || !obj.dimensions) continue;
    if (!belongsToWall(obj, wallId)) continue;
    const bottom = (obj.center.y ?? 0) - (obj.dimensions.y ?? 0) / 2;
    if (!(bottom > counterTopY) || !overSplash(obj)) continue;
    if (lowest == null || bottom < lowest) lowest = bottom;
  }
  return lowest;
}

/** The vanity splash's height above its counter on this wall, in metres. */
export function vanitySplashHeightM(wall, room, overSplash, counterTopY) {
  const { vanity } = readBacksplashSpec(wall);
  const mirrorBottomY =
    vanity?.stop === 'mirror'
      ? mirrorBottomOverSplash(room, wall?.id, overSplash, counterTopY)
      : null;
  return vanityBandHeightM(vanity, {
    counterTopY,
    mirrorBottomY,
    wallTopY: wall ? wallTopY(wall) : null,
  });
}

/** The tub backsplash's height above the tub on this wall, in metres. */
export function tubBandHeight(wall, { tubTopY, wallTopY }) {
  return tubBandHeightM(readBacksplashSpec(wall).tub, { tubTopY, wallTopY });
}

/** True when the wall's setting says it has no vanity splash. */
export const vanitySplashOff = (wall) =>
  readBacksplashSpec(wall).vanity?.off === true;

/** True when the wall's setting says it has no tub backsplash. */
export const tubBandOff = (wall) => readBacksplashSpec(wall).tub?.off === true;
